import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Link2, Unplug, CheckCircle2, AlertTriangle, ExternalLink, RefreshCw, MapPin, TrendingUp } from "lucide-react";
import ResearchModeSwitch from "@/pages/research/ResearchModeSwitch";
import FlipSettingsForm from "@/pages/flip/FlipSettingsForm";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { PRICE_ALERT_SETTING_KEY, getPriceAlertThresholdPct } from "@/lib/priceAlert";

const REDIRECT_INSTRUCTIONS = `${window.location.origin}/api/ebay/callback`;

export default function EbaySettings({ trigger }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [fetchingPolicies, setFetchingPolicies] = useState(false);
  const [policyData, setPolicyData] = useState(null); // {fulfillment_policies,payment_policies,return_policies,locations,errors}
  const [showLocForm, setShowLocForm] = useState(false);
  const [creatingLoc, setCreatingLoc] = useState(false);
  const [loc, setLoc] = useState({
    merchant_location_key: "home-warehouse",
    name: "Home Warehouse",
    phone: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_or_province: "",
    postal_code: "",
    country: "US",
  });
  const [creds, setCreds] = useState({ client_id: "", client_secret: "", dev_id: "", runame: "" });
  const [cfg, setCfg] = useState({
    merchant_location_key: "",
    fulfillment_policy_id: "",
    payment_policy_id: "",
    return_policy_id: "",
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/ebay/status");
      setStatus(data);
      if (data?.config) {
        setCfg((c) => ({
          merchant_location_key: data.config.merchant_location_key || c.merchant_location_key,
          fulfillment_policy_id: data.config.fulfillment_policy_id || c.fulfillment_policy_id,
          payment_policy_id: data.config.payment_policy_id || c.payment_policy_id,
          return_policy_id: data.config.return_policy_id || c.return_policy_id,
        }));
      }
      return data;
    } catch (e) {
      console.warn("ebay/status refresh failed", e);
      return null;
    }
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  // Listen for the OAuth callback tab to postMessage us on success.
  useEffect(() => {
    const onMessage = (evt) => {
      const d = evt.data;
      if (d && d.source === "snaplist-ebay") {
        if (d.status === "connected") {
          toast.success("eBay connected");
          refresh();
        } else {
          toast.error("eBay authorization failed. Try again.");
        }
      }
    };
    const onStorage = (evt) => {
      if (evt.key === "snaplist_ebay_oauth" && evt.newValue) {
        try {
          const v = JSON.parse(evt.newValue);
          if (v.status === "connected") { toast.success("eBay connected"); refresh(); }
        } catch (e) { /* ignore */ }
      }
    };
    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh]);

  const [authUrl, setAuthUrl] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const [submittingCode, setSubmittingCode] = useState(false);

  const submitManualCode = async () => {
    if (!manualCode.trim()) { toast.error("Paste the auth code first"); return; }
    setSubmittingCode(true);
    try {
      await api.post("/ebay/manual-code", { code: manualCode.trim() });
      toast.success("eBay connected via manual code");
      setManualCode("");
      setAuthUrl(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Token exchange failed — code may be expired");
    } finally {
      setSubmittingCode(false);
    }
  };

  const connect = () => {
    setLoading(true);
    // CRITICAL: open the popup BEFORE the async fetch so Safari treats it as
    // user-initiated. If we open after `await`, the user-gesture context is
    // lost and Safari blocks the popup.
    const popup = window.open("about:blank", "snaplist_ebay_oauth", "width=560,height=720");

    api.get("/ebay/connect")
      .then(({ data }) => {
        setAuthUrl(data.url);
        if (popup && !popup.closed) {
          // Navigate the pre-opened popup to eBay's auth URL.
          try { popup.location.href = data.url; } catch (e) { /* cross-origin OK */ }
          toast.info("Sign in to eBay in the new window — we'll detect it automatically.");
        } else {
          // Popup was blocked (e.g., Safari with strict blocker). Show inline
          // link the user can click — native <a target="_blank"> bypasses popup
          // blockers because it counts as user-initiated navigation.
          toast.warning("Popup blocked — use the 'Open eBay sign-in' link below.", { duration: 6000 });
        }

        // Poll status until connected (~2 min max) as a backup for cases where
        // postMessage/localStorage don't fire (e.g., popup opens same origin).
        let attempts = 0;
        const timer = setInterval(async () => {
          attempts += 1;
          const s = await refresh();
          if (s && s.connected) {
            clearInterval(timer);
            setAuthUrl(null);
            toast.success("eBay connected");
          } else if (attempts > 60) {
            clearInterval(timer);
          }
        }, 2000);
      })
      .catch((e) => {
        try { if (popup && !popup.closed) popup.close(); } catch (err) { /* ignore */ }
        toast.error(e?.response?.data?.detail || "Cannot start OAuth");
      })
      .finally(() => setLoading(false));
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect eBay?")) return;
    await api.post("/ebay/disconnect");
    toast.success("eBay disconnected");
    refresh();
  };

  const saveConfig = async () => {
    for (const k of Object.keys(cfg)) {
      if (!cfg[k].trim()) { toast.error(`${k} is required`); return; }
    }
    setLoading(true);
    try {
      await api.post("/ebay/config", cfg);
      toast.success("Listing policies saved");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const saveCreds = async () => {
    const payload = {};
    for (const k of ["client_id", "client_secret", "dev_id", "runame"]) {
      if (creds[k].trim()) payload[k] = creds[k].trim();
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Nothing to save — paste at least one value");
      return;
    }
    setSavingCreds(true);
    try {
      await api.post("/ebay/credentials", payload);
      toast.success("eBay credentials saved");
      setCreds({ client_id: "", client_secret: "", dev_id: "", runame: "" });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingCreds(false);
    }
  };

  const [switchingMode, setSwitchingMode] = useState(false);
  const setLiveMode = async (live) => {
    setSwitchingMode(true);
    try {
      await api.post("/ebay/mode", { env: live ? "production" : "sandbox" });
      toast.success(live ? "Live mode on — real eBay" : "Test mode on — eBay sandbox");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't switch mode");
    } finally {
      setSwitchingMode(false);
    }
  };

  const clearCreds = async () => {
    if (!window.confirm("Clear stored eBay developer credentials?")) return;
    try {
      await api.delete("/ebay/credentials");
      toast.success("Credentials cleared");
      refresh();
    } catch (e) {
      toast.error("Clear failed");
    }
  };

  const fetchPolicies = async () => {
    setFetchingPolicies(true);
    try {
      const { data } = await api.get("/ebay/policies");
      setPolicyData(data);
      const total =
        (data.fulfillment_policies?.length || 0) +
        (data.payment_policies?.length || 0) +
        (data.return_policies?.length || 0) +
        (data.locations?.length || 0);
      if (total === 0) {
        const err = Object.values(data.errors || {})[0] || "";
        if (String(err).includes("not eligible for Business Policy")) {
          toast.warning("Opt in to eBay Business Policies first (see banner)");
        } else {
          toast.info("No policies or locations found yet — create them below");
        }
      } else {
        toast.success(`Loaded ${total} item(s) from eBay`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch failed");
    } finally {
      setFetchingPolicies(false);
    }
  };

  const createLocation = async () => {
    for (const k of ["merchant_location_key", "name", "address_line_1", "city", "state_or_province", "postal_code"]) {
      if (!String(loc[k] || "").trim()) { toast.error(`${k} is required`); return; }
    }
    setCreatingLoc(true);
    try {
      const { data } = await api.post("/ebay/location", loc);
      toast.success(data.already_existed ? "Location already existed — using it" : "Location created on eBay");
      setCfg((c) => ({ ...c, merchant_location_key: data.merchant_location_key }));
      setShowLocForm(false);
      fetchPolicies();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreatingLoc(false);
    }
  };

  const [optingIn, setOptingIn] = useState(false);
  const [creatingPolicies, setCreatingPolicies] = useState(false);

  const optInAndCreatePolicies = async () => {
    setOptingIn(true);
    try {
      await api.post("/ebay/opt-in");
      toast.success("Opted in to eBay Business Policies");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opt-in failed");
      setOptingIn(false);
      return;
    }
    setOptingIn(false);
    setCreatingPolicies(true);
    try {
      const { data } = await api.post("/ebay/default-policies");
      toast.success("Default policies created on eBay");
      setCfg((c) => ({
        ...c,
        payment_policy_id: data.payment_policy_id || c.payment_policy_id,
        return_policy_id: data.return_policy_id || c.return_policy_id,
        fulfillment_policy_id: data.fulfillment_policy_id || c.fulfillment_policy_id,
      }));
      fetchPolicies();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Policy creation failed — try Fetch");
    } finally {
      setCreatingPolicies(false);
    }
  };

  const c = status?.credentials_configured || {};
  const preview = status?.credentials_preview || {};
  const missing = Object.entries(c).filter(([, v]) => !v).map(([k]) => k);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            eBay Sell Setup
            {status?.env && (
              <Badge variant="secondary" className="font-mono text-[10px] uppercase">{status.env}</Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            Publish drafts as real eBay listings — fully editable in the eBay mobile app and Seller Hub.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <ResearchModeSwitch />
          <FlipSettingsForm />
          {/* Draft defaults — stored in browser localStorage, used by the
              DraftCreator pre-fill panel. Lives at the top so you can tune
              it quickly without starting a new draft. */}
          {/* Connect */}
          <section className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Connect your eBay account</h3>
              {status?.connected ? (
                status?.needs_reconnect ? (
                  <Badge className="bg-amber-500 text-white hover:bg-amber-600">Reconnect required</Badge>
                ) : (
                  <Badge className="bg-emerald-600 text-white hover:bg-emerald-700">Connected</Badge>
                )
              ) : (
                <Badge variant="secondary">Not connected</Badge>
              )}
            </div>

            {status?.connected && status?.needs_reconnect && (
              <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <div className="mb-1 flex items-center gap-1 font-semibold">
                  <AlertTriangle className="h-3.5 w-3.5" /> Reconnect to grant listing permission
                </div>
                <p className="mb-2">
                  Your existing connection is missing the <code className="rounded bg-amber-100 px-1">sell.inventory</code> permission that Post-to-eBay needs. Click reconnect and re-approve on eBay — takes 10 seconds.
                </p>
                <Button
                  onClick={async () => {
                    try { await api.post("/ebay/disconnect"); } catch { /* ignore */ }
                    await refresh();
                    connect();
                  }}
                  disabled={loading}
                  size="sm"
                  className="w-full rounded-full bg-amber-600 text-white hover:bg-amber-700"
                  data-testid="ebay-reconnect"
                >
                  {loading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
                  Reconnect eBay
                </Button>
              </div>
            )}

            {status?.connected && !status?.needs_reconnect && status?.missing_recommended_scopes?.length > 0 && (
              <div className="mb-3 rounded-md border border-sky-300 bg-sky-50 p-3 text-xs text-sky-900">
                <div className="mb-1 flex items-center gap-1 font-semibold">
                  <AlertTriangle className="h-3.5 w-3.5" /> Reconnect to enable Promoted Listings
                </div>
                <p className="mb-2">
                  Your connection doesn't include the <code className="rounded bg-sky-100 px-1">sell.marketing</code> permission, so promotion rates set on drafts aren't applied on eBay. Reconnect to grant it — takes 10 seconds.
                </p>
                <Button
                  onClick={async () => {
                    try { await api.post("/ebay/disconnect"); } catch { /* ignore */ }
                    await refresh();
                    connect();
                  }}
                  disabled={loading}
                  size="sm"
                  className="w-full rounded-full bg-sky-600 text-white hover:bg-sky-700"
                  data-testid="ebay-reconnect-marketing"
                >
                  {loading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
                  Reconnect eBay
                </Button>
              </div>
            )}

            {status?.connected ? (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">
                  {status?.env === "production" ? (
                    <>Signed in to your <strong className="text-emerald-700">real eBay account</strong>. Every push/publish creates a live listing on ebay.com with real fees.</>
                  ) : (
                    <>Signed in as your sandbox seller. Listings appear on <strong>sandbox.ebay.com</strong> only — no real buyers.</>
                  )}
                  <br />Refresh token is stored encrypted.
                </p>
                <Button variant="outline" size="sm" className="rounded-full" onClick={disconnect} data-testid="ebay-disconnect">
                  <Unplug className="mr-1 h-3 w-3" /> Disconnect
                </Button>
              </div>
            ) : (
              <>
                <p className="mb-3 text-xs text-slate-600">
                  Sign in with your eBay seller account so SnapList can publish listings for you. You'll approve access on eBay's own page — SnapList never sees your eBay password.
                </p>
                {status && !status.fully_configured && !status.is_admin ? (
                  <p className="mb-3 rounded-md bg-amber-50 p-2 text-xs text-amber-900" data-testid="ebay-connect-unavailable">
                    eBay sign-in isn't available right now. Please try again later.
                  </p>
                ) : null}
                <Button
                  onClick={connect}
                  disabled={loading || !status?.fully_configured}
                  className="w-full rounded-full bg-[#0053DA] text-white hover:bg-[#0041B0]"
                  data-testid="ebay-connect"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
                  Connect eBay
                </Button>
                {authUrl && (
                  <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs">
                    <p className="mb-2 text-amber-900">
                      If a popup didn't open (Safari, Firefox strict mode, ad-blocker), use this direct link — it opens in a new tab and bypasses popup blockers:
                    </p>
                    <a
                      href={authUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="ebay-connect-fallback-link"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-600 px-4 py-2 font-medium text-white transition-colors hover:bg-amber-700"
                    >
                      <ExternalLink className="h-4 w-4" /> Open eBay sign-in
                    </a>
                    <p className="mt-2 text-[10px] text-amber-800">
                      After you approve access, this window auto-updates within a few seconds.
                    </p>
                  </div>
                )}
                <details className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
                  <summary className="cursor-pointer font-semibold text-slate-800">
                    Stuck on eBay's "Thank You" page? Paste the auth code manually
                  </summary>
                  <p className="mt-2 text-slate-600">
                    If eBay shows <em>"Authorization successfully completed"</em> but SnapList doesn't update, the RuName's Auth-Accepted-URL on eBay isn't wired to us. Workaround: after clicking Agree, look at your browser's address bar — if the URL contains <code className="rounded bg-slate-100 px-1">?code=...</code>, paste just that <code>code</code> value here.
                  </p>
                  <Input
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="v^1.1#i^1#...  (paste auth code)"
                    className="mt-2 font-mono text-xs"
                    data-testid="ebay-manual-code-input"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Button
                    onClick={submitManualCode}
                    disabled={submittingCode}
                    size="sm"
                    className="mt-2 w-full rounded-full bg-slate-900 text-white hover:bg-slate-800"
                    data-testid="ebay-manual-code-submit"
                  >
                    {submittingCode ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
                    Exchange code for token
                  </Button>
                </details>
              </>
            )}
          </section>

          <DraftDefaultsSection />

          {/* Credentials section — admin only */}
          {status?.is_admin && (
          <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="ebay-dev-credentials-section">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">App developer keys <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] uppercase text-slate-500">Admin</span></h3>
              {status?.fully_configured ? (
                <Badge className="bg-emerald-600 text-white hover:bg-emerald-700">
                  <CheckCircle2 className="mr-1 h-3 w-3" /> Set
                </Badge>
              ) : (
                <Badge className="bg-amber-500 text-white hover:bg-amber-600">
                  <AlertTriangle className="mr-1 h-3 w-3" /> Missing
                </Badge>
              )}
            </div>
            <div className="mb-3 flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="ebay-live-mode-row">
              <div>
                <p className="text-sm font-medium text-slate-900">Live mode</p>
                <p className="text-[11px] text-slate-500" data-testid="ebay-live-mode-status">
                  {status?.env === "production" ? "On — real eBay listings" : "Off — eBay test (sandbox)"}
                </p>
              </div>
              <Switch
                checked={status?.env === "production"}
                disabled={switchingMode}
                onCheckedChange={setLiveMode}
                data-testid="ebay-live-mode-switch"
              />
            </div>
            <p className="text-xs text-slate-600">
              Paste your <strong>{status?.env === "production" ? "Production" : "Sandbox"}</strong> keys from <a href="https://developer.ebay.com/my/keys" target="_blank" rel="noreferrer" className="text-[#0053DA] hover:underline">developer.ebay.com/my/keys</a>. They're stored encrypted in MongoDB — no .env editing required.
            </p>

            <div className="mt-3 space-y-3">
              {[
                ["client_id", "EBAY_CLIENT_ID (App ID)", false],
                ["client_secret", "EBAY_CLIENT_SECRET (Cert ID)", true],
                ["dev_id", "EBAY_DEV_ID", false],
                ["runame", "EBAY_RUNAME", false],
              ].map(([k, label, secret]) => {
                const ok = c[k];
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between">
                      <Label className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{label}</Label>
                      {ok ? (
                        <span className="ml-2 min-w-0 truncate text-[11px] font-mono text-emerald-700">saved · {preview[k]}</span>
                      ) : (
                        <span className="text-[11px] font-mono text-amber-600">not set</span>
                      )}
                    </div>
                    <Input
                      type={secret ? "password" : "text"}
                      value={creds[k]}
                      placeholder={ok ? "paste to replace…" : "paste here"}
                      onChange={(e) => setCreds((s) => ({ ...s, [k]: e.target.value }))}
                      data-testid={`ebay-cred-${k}`}
                      className="mt-1 font-mono text-sm"
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>
                );
              })}

              <div className="flex gap-2">
                <Button
                  onClick={saveCreds}
                  disabled={savingCreds}
                  size="sm"
                  className="flex-1 rounded-full bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="ebay-save-credentials"
                >
                  {savingCreds ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Save credentials
                </Button>
                {(c.client_id || c.client_secret || c.runame || c.dev_id) && (
                  <Button
                    onClick={clearCreds}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-slate-500 hover:text-red-600"
                    data-testid="ebay-clear-credentials"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            <p className="mt-3 text-[11px] text-slate-500">
              When registering the RuName on developer.ebay.com, use this Auth Accepted URL:
            </p>
            <code className="mt-1 block break-all rounded bg-slate-900 p-2 text-[11px] text-emerald-300">{REDIRECT_INSTRUCTIONS}</code>
          </section>
          )}


          {/* Policies config */}
          <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="ebay-policies-section">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">3 · Listing policies</h3>
              {status?.policies_configured ? (
                <Badge className="bg-emerald-600 text-white hover:bg-emerald-700">Set</Badge>
              ) : (
                <Badge variant="secondary">Required</Badge>
              )}
            </div>
            <p className="mb-3 text-xs text-slate-600">
              eBay needs a location + fulfillment/payment/return policy on every offer.
              Fetch what's already in your account, or create a location right here.
            </p>

            <div className="mb-3 flex gap-2">
              <Button
                onClick={fetchPolicies}
                disabled={fetchingPolicies || !status?.connected}
                size="sm"
                variant="outline"
                className="flex-1 rounded-full"
                data-testid="ebay-fetch-policies"
              >
                {fetchingPolicies ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
                Fetch from eBay
              </Button>
              <Button
                onClick={() => setShowLocForm((s) => !s)}
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={!status?.connected}
                data-testid="ebay-toggle-loc-form"
              >
                <MapPin className="mr-1 h-3 w-3" /> New location
              </Button>
            </div>

            {/* Business Policy opt-in banner + one-click fix */}
            {policyData?.errors && Object.values(policyData.errors).some((e) => String(e).includes("not eligible for Business Policy")) && (
              <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <div className="mb-1 flex items-center gap-1 font-semibold">
                  <AlertTriangle className="h-3.5 w-3.5" /> Business Policies not enabled
                </div>
                <p className="mb-2">
                  Your seller account isn't opted in to eBay's Business Policies yet. One click below will opt you in <em>and</em> create sensible default Payment / Return / Fulfillment policies — no need to visit eBay's site.
                </p>
                <Button
                  onClick={optInAndCreatePolicies}
                  disabled={optingIn || creatingPolicies}
                  size="sm"
                  className="w-full rounded-full bg-amber-600 text-white hover:bg-amber-700"
                  data-testid="ebay-optin-and-create"
                >
                  {optingIn ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" /> Opting in…</> :
                   creatingPolicies ? <><Loader2 className="mr-2 h-3 w-3 animate-spin" /> Creating policies…</> :
                   "Opt in + create default policies"}
                </Button>
              </div>
            )}

            {/* Inline location creator */}
            {showLocForm && (
              <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                <p className="mb-2 text-xs font-semibold text-slate-800">Create a merchant location</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ["merchant_location_key", "Location key", "e.g. home-warehouse"],
                    ["name", "Display name", "Home Warehouse"],
                    ["phone", "Phone (optional)", "+15551234567"],
                    ["postal_code", "ZIP / Postal", "10001"],
                    ["address_line_1", "Address line 1", "123 Main St"],
                    ["address_line_2", "Address line 2", "Apt 4B"],
                    ["city", "City", "New York"],
                    ["state_or_province", "State", "NY"],
                    ["country", "Country (ISO-2)", "US"],
                  ].map(([k, label, ph]) => (
                    <div key={k} className={k === "address_line_1" || k === "address_line_2" ? "col-span-2" : ""}>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
                      <Input
                        value={loc[k]}
                        onChange={(e) => setLoc((v) => ({ ...v, [k]: e.target.value }))}
                        placeholder={ph}
                        data-testid={`ebay-loc-${k}`}
                        className="mt-1 text-sm"
                      />
                    </div>
                  ))}
                </div>
                <Button
                  onClick={createLocation}
                  disabled={creatingLoc}
                  size="sm"
                  className="mt-3 w-full rounded-full bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="ebay-create-location"
                >
                  {creatingLoc ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : null}
                  Create on eBay
                </Button>
              </div>
            )}

            {/* Policy fields — dropdown when eBay data available, text input otherwise */}
            <div className="space-y-3">
              <PolicyField
                keyName="merchant_location_key"
                label="Merchant location"
                items={policyData?.locations}
                itemToValue={(l) => l.merchantLocationKey}
                itemToLabel={(l) => `${l.name || l.merchantLocationKey} (${l.merchantLocationKey})`}
                cfg={cfg}
                setCfg={setCfg}
              />
              <PolicyField
                keyName="fulfillment_policy_id"
                label="Fulfillment policy"
                items={policyData?.fulfillment_policies}
                itemToValue={(p) => p.fulfillmentPolicyId}
                itemToLabel={(p) => `${p.name} (${p.fulfillmentPolicyId})`}
                cfg={cfg}
                setCfg={setCfg}
              />
              <PolicyField
                keyName="payment_policy_id"
                label="Payment policy"
                items={policyData?.payment_policies}
                itemToValue={(p) => p.paymentPolicyId}
                itemToLabel={(p) => `${p.name} (${p.paymentPolicyId})`}
                cfg={cfg}
                setCfg={setCfg}
              />
              <PolicyField
                keyName="return_policy_id"
                label="Return policy"
                items={policyData?.return_policies}
                itemToValue={(p) => p.returnPolicyId}
                itemToLabel={(p) => `${p.name} (${p.returnPolicyId})`}
                cfg={cfg}
                setCfg={setCfg}
              />
              <Button onClick={saveConfig} disabled={loading} size="sm" className="w-full rounded-full" data-testid="ebay-save-config">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save policies
              </Button>
            </div>
          </section>

          {status?.connected && status?.policies_configured && (
            <p className="text-center text-xs text-emerald-700">
              <CheckCircle2 className="mr-1 inline h-3 w-3" /> Ready — you can push drafts to eBay from any listing.
            </p>
          )}

          <a
            href="https://developer.ebay.com/my/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 text-xs text-[#0053DA] hover:underline"
          >
            eBay developer keys page <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function PolicyField({ keyName, label, items, itemToValue, itemToLabel, cfg, setCfg }) {
  const hasItems = Array.isArray(items) && items.length > 0;
  return (
    <div>
      <Label className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{label}</Label>
      {hasItems ? (
        <Select
          value={cfg[keyName] || ""}
          onValueChange={(v) => setCfg((c) => ({ ...c, [keyName]: v }))}
        >
          <SelectTrigger className="mt-1" data-testid={`ebay-${keyName}`}>
            <SelectValue placeholder={`Choose ${label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {items.map((it) => {
              const v = itemToValue(it);
              return (
                <SelectItem key={v} value={v} data-testid={`ebay-${keyName}-option-${v}`}>
                  {itemToLabel(it)}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      ) : (
        <Input
          value={cfg[keyName]}
          onChange={(e) => setCfg((c) => ({ ...c, [keyName]: e.target.value }))}
          placeholder="paste ID"
          data-testid={`ebay-${keyName}`}
          className="mt-1 font-mono text-sm"
        />
      )}
    </div>
  );
}

const PREFILL_DEFAULTS_KEY = "snaplist.prefillDefaults";

/** Draft pre-fill defaults — persisted in the browser only. Mirrored by the
 * DraftCreator pre-fill panel so each new listing opens with these values. */
function DraftDefaultsSection() {
  const [minOffer, setMinOffer] = useState("");
  const [sku, setSku] = useState("");
  const [threshold, setThreshold] = useState(50);
  const [savedToast, setSavedToast] = useState(false);

  // Load current values on open.
  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(PREFILL_DEFAULTS_KEY) || "{}");
      setMinOffer(d.min_offer ?? "");
      setSku(d.sku ?? "");
    } catch { /* ignore */ }
    setThreshold(getPriceAlertThresholdPct());
  }, []);

  const save = () => {
    try {
      const existing = JSON.parse(localStorage.getItem(PREFILL_DEFAULTS_KEY) || "{}");
      const next = { ...existing };
      // Ad rate is now hardcoded to 3% on every new draft — scrub any legacy
      // ad_rate the user had saved under this key.
      delete next.ad_rate;
      const trimMin = String(minOffer).trim();
      const trimSku = String(sku).trim();
      if (trimMin === "") delete next.min_offer;
      else next.min_offer = trimMin;
      if (trimSku === "") delete next.sku;
      else next.sku = trimSku;
      localStorage.setItem(PREFILL_DEFAULTS_KEY, JSON.stringify(next));
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 1500);
    } catch { /* ignore */ }
  };

  const clear = () => {
    setMinOffer("");
    setSku("");
    try {
      const existing = JSON.parse(localStorage.getItem(PREFILL_DEFAULTS_KEY) || "{}");
      delete existing.ad_rate;
      delete existing.min_offer;
      delete existing.sku;
      localStorage.setItem(PREFILL_DEFAULTS_KEY, JSON.stringify(existing));
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 1500);
    } catch { /* ignore */ }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="draft-defaults-section">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Draft defaults</h3>
        {savedToast && (
          <Badge className="bg-emerald-600 text-white hover:bg-emerald-700">
            <CheckCircle2 className="mr-1 h-3 w-3" /> Saved
          </Badge>
        )}
      </div>
      <p className="mb-3 text-[11px] text-slate-500">
        Pre-fill these on every new draft. Stored locally in this browser only.
      </p>
      <div className="grid grid-cols-1 gap-2">
        <div>
          <Label htmlFor="defaults-min-offer" className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
            Min offer (USD)
          </Label>
          <Input
            id="defaults-min-offer"
            value={minOffer}
            onChange={(e) => setMinOffer(e.target.value)}
            placeholder="e.g. 15.00"
            inputMode="decimal"
            className="mt-1 h-8 font-mono text-xs"
            data-testid="defaults-min-offer-input"
          />
        </div>
        <p className="text-[10px] text-slate-400">
          Promoted Listings ad rate is fixed at <span className="font-semibold">3%</span> on every new draft. You can override it per-draft in the editor.
        </p>
      </div>
      <div className="mt-2">
        <Label htmlFor="defaults-sku" className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
          Last SKU
        </Label>
        <div className="mt-1 flex gap-2">
          <Input
            id="defaults-sku"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            placeholder="e.g. EB319"
            className="h-8 flex-1 font-mono text-xs"
            data-testid="defaults-sku-input"
          />
          <Button
            onClick={() => {
              const next = String(sku).trim();
              if (!next) {
                toast.error("Enter a starting SKU first");
                return;
              }
              try {
                const existing = JSON.parse(localStorage.getItem(PREFILL_DEFAULTS_KEY) || "{}");
                existing.sku = next;
                localStorage.setItem(PREFILL_DEFAULTS_KEY, JSON.stringify(existing));
                toast.success(`SKU reset — next draft starts after ${next}`);
                setSavedToast(true);
                setTimeout(() => setSavedToast(false), 1500);
              } catch { /* ignore */ }
            }}
            size="sm"
            variant="outline"
            className="h-8 rounded-full border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="defaults-sku-reset"
            title="Save this value as the next draft's starting SKU"
          >
            Reset
          </Button>
        </div>
        <p className="mt-0.5 text-[10px] text-slate-400">
          Type a new starting SKU and click <span className="font-semibold">Reset</span>. Every new draft and duplicate will use this exact SKU until you reset it again.
        </p>
      </div>
      <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="price-alert-threshold-block">
        <div className="mb-1 flex items-center justify-between">
          <Label htmlFor="price-alert-slider" className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-slate-500">
            <TrendingUp className="h-3 w-3" /> Price alert threshold
          </Label>
          <span className="rounded-full bg-white px-2 py-0.5 font-mono text-xs font-semibold text-slate-900 shadow-sm" data-testid="price-alert-threshold-value">
            +{threshold}%
          </span>
        </div>
        <input
          id="price-alert-slider"
          type="range"
          min={10}
          max={200}
          step={5}
          value={threshold}
          onChange={(e) => {
            const n = Number(e.target.value);
            setThreshold(n);
            try {
              localStorage.setItem(PRICE_ALERT_SETTING_KEY, String(n));
            } catch { /* ignore */ }
          }}
          className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-[#0053DA]"
          data-testid="price-alert-threshold-slider"
        />
        <div className="mt-1 flex justify-between text-[9px] font-mono text-slate-400">
          <span>+10% (sensitive)</span>
          <span>+200% (loud wins only)</span>
        </div>
        <p className="mt-2 text-[10px] text-slate-500">
          Nudges fire when a comparable eBay sold price is at least <span className="font-semibold">+{threshold}%</span> above your draft price. Lower = more alerts, higher = only big misses.
        </p>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button
          onClick={save}
          size="sm"
          className="h-8 flex-1 rounded-full bg-[#0053DA] text-white hover:bg-[#0041B0]"
          data-testid="defaults-save"
        >
          Save defaults
        </Button>
        <Button
          onClick={clear}
          size="sm"
          variant="outline"
          className="h-8 rounded-full border-slate-300 text-slate-700 hover:bg-slate-50"
          data-testid="defaults-clear"
        >
          Clear
        </Button>
      </div>
    </section>
  );
}

